 
// src/pages/DevTalent.js
import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import imagesLoaded from 'imagesloaded';
 
const API_URL = 'https://api.securxperts.com:8000';
const ENDPOINT = '/forms/contact';
 
/* -------------------- CONTACT FORM ----------------------- */
const ContactForm = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    country_code: '',
    phoneno: '',
    location: '',
    purpose: '',
    message: '',
  });
 
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
 
  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
 
  const validate = () => {
    if (!form.name.trim()) return 'Name is required';
    if (!form.email.trim()) return 'Email is required';
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(form.email)) return 'Enter a valid email';
    if (!form.country_code.trim()) return 'Country code is required';
    if (!form.phoneno.trim()) return 'Phone number is required';
    if (!form.location.trim()) return 'Location is required';
    if (!form.purpose.trim()) return 'Purpose is required';
    if (!form.message.trim()) return 'Message is required';
    return null;
  };
 
  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
 
    const err = validate();
    if (err) {
      setStatus({ type: 'error', msg: err });
      return;
    }
 
    setSubmitting(true);
    try {
      const payload = { ...form };
 
      const resp = await fetch(`${API_URL}${ENDPOINT}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
 
      if (resp.ok) {
        let data = null;
        try {
          data = await resp.json();
        } catch {
          // ignore parse errors
        }
        setStatus({
          type: 'success',
          msg: data?.message || 'Thank you! Your request has been submitted.',
        });
        setForm({
          name: '',
          email: '',
          country_code: '',
          phoneno: '',
          location: '',
          purpose: '',
          message: '',
        });
      } else {
        let errText = `Server responded with ${resp.status}`;
        try {
          const json = await resp.json();
          if (json?.error) errText = json.error;
          else if (json?.message) errText = json.message;
        } catch {
          const txt = await resp.text().catch(() => null);
          if (txt) errText = txt;
        }
        setStatus({ type: 'error', msg: `Submission failed: ${errText}` });
      }
    } catch (error) {
      console.error('DevTalent contact submit error:', error);
      setStatus({ type: 'error', msg: 'Network error. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };
 
  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-3xl mx-auto bg-white shadow-lg shadow-purple-100/80 border border-purple-50 p-6 md:p-10 rounded-3xl"
      aria-label="DevTalent Enquiry Form"
    >
      <h3 className="text-2xl md:text-3xl font-bold text-center mb-6 text-slate-900">
        Start Hiring with DevTalent
      </h3>
 
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input
          name="name"
          value={form.name}
          onChange={handleChange}
          placeholder="Full Name"
          required
          className="w-full p-3 rounded-xl bg-purple-50/60 placeholder-slate-400 text-slate-900 outline-none border border-purple-100 focus:border-purple-400 focus:bg-white transition"
        />
 
        <input
          name="email"
          type="email"
          value={form.email}
          onChange={handleChange}
          placeholder="Email"
          required
          className="w-full p-3 rounded-xl bg-purple-50/60 placeholder-slate-400 text-slate-900 outline-none border border-purple-100 focus:border-purple-400 focus:bg-white transition"
        />
 
        <input
          name="country_code"
          value={form.country_code}
          onChange={handleChange}
          placeholder="Country Code (e.g. +91)"
          required
          className="w-full p-3 rounded-xl bg-purple-50/60 placeholder-slate-400 text-slate-900 outline-none border border-purple-100 focus:border-purple-400 focus:bg-white transition"
        />
 
        <input
          name="phoneno"
          value={form.phoneno}
          onChange={handleChange}
          placeholder="Phone Number"
          type="tel"
          required
          className="w-full p-3 rounded-xl bg-purple-50/60 placeholder-slate-400 text-slate-900 outline-none border border-purple-100 focus:border-purple-400 focus:bg-white transition"
        />
 
        <input
          name="location"
          value={form.location}
          onChange={handleChange}
          placeholder="Company Location (City, Country)"
          required
          className="w-full p-3 rounded-xl bg-purple-50/60 placeholder-slate-400 text-slate-900 outline-none border border-purple-100 focus:border-purple-400 focus:bg-white transition md:col-span-2"
        />
 
        <input
          name="purpose"
          value={form.purpose}
          onChange={handleChange}
          placeholder="Purpose (Hire / Scale Team / Partnership)"
          required
          className="w-full p-3 rounded-xl bg-purple-50/60 placeholder-slate-400 text-slate-900 outline-none border border-purple-100 focus:border-purple-400 focus:bg-white transition md:col-span-2"
        />
 
        <textarea
          name="message"
          value={form.message}
          onChange={handleChange}
          placeholder="Tell us about the roles, skills and team size you’re hiring for"
          rows={4}
          required
          className="w-full p-3 rounded-xl bg-purple-50/60 placeholder-slate-400 text-slate-900 outline-none border border-purple-100 focus:border-purple-400 focus:bg-white transition md:col-span-2 resize-none"
        />
      </div>
 
      <button
        type="submit"
        disabled={submitting}
        className="bg-gradient-to-r from-purple-600 to-indigo-500 hover:from-purple-700 hover:to-indigo-600 transition px-6 py-3 rounded-full font-semibold w-full mt-6 text-white disabled:opacity-50"
      >
        {submitting ? 'Submitting...' : 'Start Hiring Now'}
      </button>
 
      {status && (
        <p
          role="status"
          className={`text-center mt-4 text-sm ${
            status.type === 'success' ? 'text-emerald-500' : 'text-red-500'
          }`}
        >
          {status.msg}
        </p>
      )}
 
      <p className="text-xs mt-4 text-slate-500 text-center">
        Your details will remain confidential and used only for enquiry purposes.
      </p>
    </form>
  );
};
 
/* -------------------- MAIN PAGE ----------------------- */
const DevTalent = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [showScroll, setShowScroll] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
 
  // hero video ref
  const heroVideoRef = useRef(null);
 
  // Image load -> hide loading screen
  useEffect(() => {
    imagesLoaded(
      document.querySelectorAll('.content__item-img'),
      { background: true },
      () => {
        document.body.classList.remove('loading');
        setIsLoading(false);
      }
    );
  }, []);
 
  // Parallax scroll for cards (kept for future visual effects, safe if no items)
  useEffect(() => {
    const items = document.querySelectorAll('.content__item');
 
    const handleScroll = () => {
      items.forEach((item) => {
        const img = item.querySelector('.content__item-img');
        const wrapper = img?.parentNode;
        if (!img || !wrapper) return;
 
        const rect = item.getBoundingClientRect();
        const progress = Math.min(
          Math.max(
            (window.innerHeight - rect.top) /
              (window.innerHeight + rect.height),
            0
          ),
          1
        );
 
        const translateY = progress * 100 - 50;
        const rotate = progress * -45;
 
        img.style.transform = `translateY(${translateY}px)`;
        wrapper.style.transform = `rotate3d(1, 0.3, 0.1, ${rotate}deg)`;
      });
    };
 
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
 
  // Circular scroll progress button visibility & progress
  useEffect(() => {
    const circumference = 307.919;
    const handleProgress = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress =
        docHeight > 0 ? Math.min(Math.max(scrollTop / docHeight, 0), 1) : 0;
      setScrollProgress(circumference * progress);
      setShowScroll(scrollTop > 600);
    };
 
    window.addEventListener('scroll', handleProgress);
    handleProgress();
    return () => window.removeEventListener('scroll', handleProgress);
  }, []);
 
  // Restart hero video when user scrolls back to top
  useEffect(() => {
    let wasNotAtTop = false;
 
    const handleVideoReset = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const isAtTop = scrollTop < 50;
 
      if (isAtTop && wasNotAtTop && heroVideoRef.current) {
        heroVideoRef.current.currentTime = 0;
        heroVideoRef.current.play().catch(() => {});
      }
 
      wasNotAtTop = !isAtTop;
    };
 
    window.addEventListener('scroll', handleVideoReset);
    handleVideoReset();
 
    return () => window.removeEventListener('scroll', handleVideoReset);
  }, []);
 
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
 
    if (heroVideoRef.current) {
      heroVideoRef.current.currentTime = 0;
      heroVideoRef.current.play().catch(() => {});
    }
  };
 
  // data for framework and features
  const frameworkItems = [
    {
      title: 'Live Coding & Assessment Hub',
      desc: 'Run coding challenges and MCQs together in one seamless session, no platform switching.',
      index: '01',
    },
    {
      title: 'Effortless Developer Recruitment',
      desc: 'Post roles and instantly receive candidates shortlisted by skills and experience.',
      index: '02',
    },
    {
      title: 'Verified Talent Pool',
      desc: 'Access pre-assessed developers with portfolios, tech stacks and performance data.',
      index: '03',
    },
    {
      title: 'Role-Specific Dashboards',
      desc: 'Custom dashboards let managers and recruiters view only relevant insights.',
      index: '04',
    },
  ];
 
  const featureCards = [
    {
      title: 'Job & Evaluation Configuration',
      desc: 'Set up job openings and assessment workflows effortlessly within a single interface. Define evaluation metrics, scoring patterns and selection criteria to ensure structured recruitment and streamlined candidate review.',
    },
    {
      title: 'Intelligent Skill Assessment',
      desc: 'Create live coding challenges and MCQs with automatic scoring and instant insights. Measure candidate proficiency accurately, minimise manual intervention and provide actionable feedback to hiring teams efficiently.',
    },
    {
      title: 'Automated Certification & Credentialing',
      desc: 'Automatically issue verified certificates on assessment completion. Ensure candidates receive credible recognition while maintaining organisational branding and compliance-ready records.',
    },
    {
      title: 'Real-Time Performance Tracking',
      desc: 'Track candidate engagement, test progression and skill outcomes via live dashboards. Identify gaps, strengths and participation trends to enable timely intervention and informed recruitment decisions.',
    },
    {
      title: 'Central Talent Repository',
      desc: 'Maintain portfolios, evaluation histories and skill data in a single hub. Recruiters and managers can access, analyse and manage talent information seamlessly, ensuring operational efficiency.',
    },
    {
      title: 'Role-Specific Reporting & Analytics',
      desc: 'Provide customised dashboards and reports for managers, recruiters and team leads. Visualise performance, track tasks and measure competencies to aid strategic workforce planning and effective oversight.',
    },
  ];
 
  return (
    <>
      <Helmet>
        <title>
          The Most Advanced Next-Gen Skill Evaluation Platform | DevTalent
        </title>
        <meta
          name="description"
          content="DevTalent helps IT companies, startups and staffing agencies hire verified developers, run live assessments, track performance, manage projects and streamline payroll from one platform."
        />
      </Helmet>
 
      {/* Loading Screen */}
      <div
        className={`fixed inset-0 bg-white flex items-center justify-center z-50 transition-opacity duration-700 ${
          isLoading ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <h1 className="text-3xl md:text-5xl font-extrabold text-purple-700 animate-pulse text-center px-4">
          DevTalent – Next-Gen Skill Evaluation
        </h1>
      </div>
 
      <Navbar />
     
 
    {/* HERO SECTION (video background but light UI like design) */}
<section className="relative overflow-hidden">
  {/* Background video */}
  <video
    ref={heroVideoRef}
    className="absolute inset-0 w-full h-full object-cover opacity-20"
    autoPlay
    loop
    muted
    playsInline
  >
    <source src="/video/dev talent.mp4" type="video/mp4" />
  </video>
 
  {/* Gradient overlay */}
  <div className="absolute inset-0 bg-gradient-to-br from-[#f7f0ff] via-white to-[#f4f9ff]" />
 
  {/* TOP-LEFT LOGO ON HERO */}
  <div className="absolute top-0 left-5 z-20">
    <img
      src="/img/dev talent.png"
      alt="Logo"
      className="w-20 h-auto"
    />
  </div>
 
  <div className="relative max-w-6xl mx-auto px-6 md:px-8 lg:px-10 pt-32 pb-20 md:pb-28 flex flex-col md:flex-row items-center gap-10">
    {/* Left Copy */}
    <div className="w-full md:w-1/2 space-y-5">
      {/* <div className="inline-flex items-center px-3 py-1 rounded-full bg-purple-50 border border-purple-100 text-xs font-semibold text-purple-700">
        <span className="w-1.5 h-1.5 rounded-full bg-purple-500 mr-2" />
        We are DevTalent
      </div> */}
 
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight mb-25">
        Premier Talent Evaluation &amp; Assessment Platform
      </h1>
 
      <p className="text-slate-600 text-sm sm:text-base leading-relaxed max-w-xl">
        DevTalent is an advanced talent acquisition and workforce management
        platform, designed for IT companies, startups, product teams and staffing
        agencies. It streamlines developer hiring, skill assessments, productivity
        tracking, project management and payroll within a single unified system.
      </p>
 
      {/* <div className="flex flex-wrap items-center gap-4 pt-2">
        <a
          href="#contact"
          className="px-6 py-3 rounded-full text-sm font-semibold bg-gradient-to-r from-purple-600 to-indigo-500 text-white shadow-lg shadow-purple-200 hover:from-purple-700 hover:to-indigo-600 transition"
        >
          Book a Demo
        </a>
        <Link
          to="/services"
          className="px-6 py-3 rounded-full text-sm font-semibold border border-purple-200 text-purple-700 bg-white/70 backdrop-blur hover:bg-purple-50 transition"
        >
          Contact Us
        </Link>
      </div> */}
    </div>
 
    {/* Right Illustration */}
    {/* Right Image */}
        <div className="flex justify-center">
            <img
              src="/img/devimg1.png"
              alt="Monitoring Dashboard"
              className="w-96"
            />
          </div>
  </div>
</section>
 
 
      {/* PAGE WRAPPER WITH FULL BG COLOR */}
      <div className="w-full min-h-screen bg-[#BB9BAC]">
        {/* MAIN CONTENT */}
        <main className="w-full">
          <div className="content content--padded max-w-6xl mx-auto px-6 md:px-8 lg:px-10 pb-20">
            {/* FULL-WIDTH VIDEO SECTION – LOOP + CLICK PAUSE/PLAY + RESTART ON REFRESH */}
            <section className="w-full py-20 px-6 md:px-0 bg-gray-50">
              <div className="max-w-7xl mx-auto">
                <div className="max-w-3xl mx-auto mb-10 text-center">
                  <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900">
                    Premier Talent Evaluation &amp; Assessment Platform Demo
                  </h2>
                  <p className="text-sm md:text-base text-slate-600 mt-3">
                    DevTalent streamlines developer hiring, skill assessments, performance
                    tracking and payroll in one platform, enabling IT teams and staffing
                    agencies to manage talent efficiently and make data-driven decisions.
                  </p>
                </div>
 
                <div className="relative rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl group">
                  <video
                    className="w-full block"
                    src="/video/dev talent.mp4"
                    muted
                    playsInline
                    autoPlay
                    loop
                    preload="auto"
                    controls={false}
                    key="lauratek-video"
                    onClick={(e) => {
                      const video = e.currentTarget;
                      video.paused ? video.play() : video.pause();
                    }}
                  >
                    Your browser does not support HTML5 video.
                  </video>
 
                  {/* ONLY Fullscreen Button – appears on hover */}
                  <div className="absolute bottom-6 right-6 md:bottom-8 md:right-8 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const video =
                          e.currentTarget.closest('div').querySelector('video');
                        if (video.requestFullscreen) video.requestFullscreen();
                        else if (video.webkitRequestFullscreen)
                          video.webkitRequestFullscreen();
                        else if (video.mozRequestFullScreen)
                          video.mozRequestFullScreen();
                        else if (video.msRequestFullscreen)
                          video.msRequestFullscreen();
                      }}
                      className="bg-black/70 hover:bg-black/90 text-white p-4 rounded-xl backdrop-blur-md transition transform hover:scale-110 shadow-2xl"
                      title="Fullscreen"
                    >
                      <svg
                        className="w-8 h-8"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4-4l-5-5"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </section>
 
           {/* ADVANCED TALENT MANAGEMENT FRAMEWORK */}
<section className="mb-16">
  <div className="text-center mb-8">
    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-purple-500">
      Framework
    </p>
    <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 mt-2">
      Advanced Talent Management Framework
    </h2>
    <p className="text-sm md:text-base text-slate-600 mt-3 max-w-2xl mx-auto">
      Live coding and assessment, effortless developer recruitment, verified talent pools
      and role-specific dashboards form the core of DevTalent’s Advanced Talent
      Management Framework.
    </p>
  </div>
 
  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-5">
    {/* Card 01 – Live Coding & Assessment Hub */}
    <div className="bg-white rounded-2xl border border-purple-100 shadow-sm shadow-purple-50 p-5 flex flex-col space-y-3">
     
 
      {/* Icon Slot – replace src with your icon */}
      <div className="w-20 h-20 rounded-xl bg-purple-50 border border-purple-100 flex items-center justify-center mb-2">
        <img
          src="/img/dev1.jpg" // 👉 your icon here
          alt="Live Coding Icon"
          className="w-18 h-18 object-contain"
        />
      </div>
 
      <h3 className="text-sm md:text-base font-bold text-slate-900">
        Live Coding &amp; Assessment Hub
      </h3>
      <p className="text-xs md:text-sm text-slate-600">
        Run coding challenges and MCQs together in one seamless session, with no platform
        switching or context loss across assessments.
      </p>
    </div>
 
    {/* Card 02 – Effortless Developer Recruitment */}
    <div className="bg-white rounded-2xl border border-purple-100 shadow-sm shadow-purple-50 p-5 flex flex-col space-y-3">
     
 
      {/* Icon Slot */}
      <div className="w-10 h-10 rounded-xl bg-purple-50 border border-purple-100 flex items-center justify-center mb-2">
        <img
          src="/img/dev2.jpg" // 👉 your icon here
          alt="Recruitment Icon"
          className="w-18 h-18 object-contain"
        />
      </div>
 
      <h3 className="text-sm md:text-base font-bold text-slate-900">
        Effortless Developer Recruitment
      </h3>
      <p className="text-xs md:text-sm text-slate-600">
        Post roles and instantly receive curated shortlists of candidates matched by skills,
        experience and job-role requirements.
      </p>
    </div>
 
    {/* Card 03 – Verified Talent Pool */}
    <div className="bg-white rounded-2xl border border-purple-100 shadow-sm shadow-purple-50 p-5 flex flex-col space-y-3">
     
 
      {/* Icon Slot */}
      <div className="w-10 h-10 rounded-xl bg-purple-50 border border-purple-100 flex items-center justify-center mb-2">
        <img
          src="/img/dev3.jpg" // 👉 your icon here
          alt="Talent Pool Icon"
          className="w-18 h-18 object-contain"
        />
      </div>
 
      <h3 className="text-sm md:text-base font-bold text-slate-900">
        Verified Talent Pool
      </h3>
      <p className="text-xs md:text-sm text-slate-600">
        Access pre-assessed developers with portfolios, tech stacks and performance data,
        so you always hire from a trusted talent pool.
      </p>
    </div>
 
    {/* Card 04 – Role-Specific Dashboards */}
    <div className="bg-white rounded-2xl border border-purple-100 shadow-sm shadow-purple-50 p-5 flex flex-col space-y-3">
     
 
      {/* Icon Slot */}
      <div className="w-10 h-10 rounded-xl bg-purple-50 border border-purple-100 flex items-center justify-center mb-2">
        <img
          src="/img/dev4.jpg" // 👉 your icon here
          alt="Dashboard Icon"
           className="w-18 h-18 object-contain"
        />
      </div>
 
      <h3 className="text-sm md:text-base font-bold text-slate-900">
        Role-Specific Dashboards
      </h3>
      <p className="text-xs md:text-sm text-slate-600">
        Custom dashboards let engineering leaders, recruiters and delivery teams see only
        the metrics, pipelines and insights relevant to their roles.
      </p>
    </div>
  </div>
</section>
 
 
           {/* Streamlined Workforce Operations Section */}
<section className="py-20">
  <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-28 items-center px-6">
 
    {/* LEFT — IMAGE PLACEHOLDER */}
    <div className="w-full flex justify-center">
      <div className="w-full max-w-lg h-80 rounded-2xl bg-gradient-to-br from-purple-50 to-indigo-50 border border-dashed border-purple-300 flex items-center justify-center text-purple-500 font-semibold">
      <img
  src="/img/devimg2.jpg"
  className="w-full max-w-4xl h-180 ml-[3rem]rounded-2xl object-cover shadow-lg"
/>
 
      </div>
    </div>
 
    {/* RIGHT — CONTENT EXACT AS YOU PROVIDED */}
    <div className="space-y-4">
      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-purple-500">
        Workforce Operations
      </p>
 
      <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900">
        Streamlined Workforce Operations
      </h2>
 
      <div className="space-y-6">
        <div>
          <h3 className="text-sm md:text-base font-semibold text-slate-900">
            Efficient Workflow Automation
          </h3>
          <p className="text-sm md:text-base text-slate-600">
            Automated workflows cut admin overhead and simplify day-to-day
            hiring and assessment, keeping operations smooth and efficient
            across teams.
          </p>
        </div>
 
        <div>
          <h3 className="text-sm md:text-base font-semibold text-slate-900">
            Strategic Talent Progression Oversight
          </h3>
          <p className="text-sm md:text-base text-slate-600">
            Monitor every stage from application to onboarding, using
            performance insights and progress reports to guide informed
            talent decisions.
          </p>
        </div>
 
        <div>
          <h3 className="text-sm md:text-base font-semibold text-slate-900">
            Advanced Platform Capabilities
          </h3>
          <p className="text-sm md:text-base text-slate-600">
            Real-time analytics, automated assessments and exportable reports
            enhance operational control, visibility and strategic workforce
            management.
          </p>
        </div>
 
        <div>
          <h3 className="text-sm md:text-base font-semibold text-slate-900">
            Integrated Compliance & Regulatory Assurance
          </h3>
          <p className="text-sm md:text-base text-slate-600">
            Audit-ready records and secure data management ensure full
            alignment with organisational policies and regulatory standards
            seamlessly.
          </p>
        </div>
      </div>
    </div>
 
  </div>
</section>
 
 
            {/* CORE TALENT MANAGEMENT FEATURES */}
            <section className="mb-16">
              <div className="text-center mb-8">
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-purple-500">
                  Services
                </p>
                <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 mt-2">
                  Core Talent Management Features
                </h2>
                <p className="text-sm md:text-base text-slate-600 mt-3 max-w-2xl mx-auto">
                  Automated skill assessments, instant certification, real-time analytics
                  and a centralised resource hub optimise workforce operations, enhance
                  engagement and deliver actionable insights for HR, IT teams and hiring
                  managers, ensuring efficient and informed talent management decisions.
                </p>
              </div>
 
              <div className="grid md:grid-cols-3 gap-5">
                {featureCards.map((feature, index) => (
                  <div
                    key={feature.title}
                    className="bg-white rounded-2xl border border-purple-100 shadow-sm shadow-purple-50 p-6 flex flex-col space-y-3"
                  >
                    <div className="w-9 h-9 rounded-xl bg-purple-50 border border-purple-100 flex items-center justify-center text-[11px] font-semibold text-purple-600 mb-1">
                      {index + 1}
                    </div>
 
                    <h3 className="text-sm md:text-base font-bold text-slate-900">
                      {feature.title}
                    </h3>
 
                    <p className="text-xs md:text-sm text-slate-600">
                      {feature.desc}
                    </p>
                  </div>
                ))}
              </div>
            </section>
 
         
 
            {/* CONTACT SECTION */}
            <section id="contact" className="mb-4">
              <div className="text-center mb-6">
                <h3 className="text-2xl md:text-3xl font-extrabold text-slate-900">
                  Tell Us About Your Hiring Needs
                </h3>
                <p className="text-sm md:text-base text-slate-600 mt-2 max-w-2xl mx-auto">
                  Whether you’re scaling a startup or building a product team, our experts
                  can help integrate DevTalent into your hiring workflow.
                </p>
              </div>
 
              <ContactForm />
            </section>
          </div>
        </main>
      </div>
 
      <Footer />
 
      {/* SIMPLE ARROW SCROLL-TO-TOP (kept but tied to showScroll now) */}
      <button
        onClick={scrollToTop}
        className={`scrollToTopBtn fixed bottom-24 right-6 z-40 transition-all duration-500 ${
          showScroll
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        <svg className="w-10 h-10 text-slate-900 drop-shadow-xl" viewBox="0 0 512 512">
          <path
            fill="currentColor"
            d="M256 108L109 296l37 37 90-90v210h48V243l90 90 37-37z"
          />
        </svg>
      </button>
 
      {/* Circular Scroll Progress Button */}
      {showScroll && (
        <div
          onClick={scrollToTop}
          className="scroll-top fixed bottom-6 right-6 z-50 cursor-pointer flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 shadow-xl hover:from-purple-600 hover:to-indigo-600 transition-all"
        >
          <svg
            className="progress-circle -rotate-90 w-12 h-12 absolute"
            viewBox="-1 -1 102 102"
          >
            <path
              d="M50,1 a49,49 0 0,1 0,98 a49,49 0 0,1 0,-98"
              fill="none"
              stroke="white"
              strokeWidth="4"
              strokeDasharray="307.919"
              strokeDashoffset={307.919 - scrollProgress}
              style={{ transition: 'stroke-dashoffset 0.1s linear' }}
            />
          </svg>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-6 h-6 text-white relative z-10"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </div>
      )}
    </>
  );
};
 
export default DevTalent;
 
 